import { FunctionComponent, useContext } from "react";
import { Navigate } from "react-router-dom";

import { UserContext } from "./UserProvider";
import {
  FeatureType,
  hidesProductCampaigns,
  TenantTypeName
} from "./models/Tenant";
import { hasRole } from "./models/User";
import { permissions, whiteLabelCampaignRoles } from "./constants";

interface AuthorizedProps {
  allow?: string[];
  feature?: FeatureType;
  deny?: string[];
  tenantTypeNames: TenantTypeName[];
}

export const Authorized: FunctionComponent<AuthorizedProps> = ({
  allow,
  feature,
  deny,
  children,
  tenantTypeNames
}) => {
  const user = useContext(UserContext);

  if (user.type === "ANONYMOUS") {
    return null;
  }

  if (allow) {
    const userHasAccess = user.roles.some(role => allow.includes(role));
    if (
      !userHasAccess ||
      (feature && user.tenant.configuration?.features?.[feature] === false)
    ) {
      return null;
    }
  } else if (deny && user.roles.every(role => !deny.includes(role))) {
    return null;
  }

  if (tenantTypeNames.length > 0) {
    const tenantAccess = tenantTypeNames.includes(user.tenant.tenantType.name);
    if (!tenantAccess) {
      return null;
    }
  }

  return <>{children}</>;
};

export const RequireRole: FunctionComponent<{
  allow: ReadonlyArray<string>;
  redirectTo?: string;
}> = ({ allow, redirectTo = "/", children }) => {
  const user = useContext(UserContext);

  if (user.type === "ANONYMOUS" || !hasRole(user, allow)) {
    return <Navigate replace to={redirectTo} />;
  }

  return <>{children}</>;
};

export const RequireProductCampaignAccess: FunctionComponent<{
  allow: ReadonlyArray<string>;
  redirectTo?: string;
}> = ({ allow, redirectTo = "/", children }) => {
  const user = useContext(UserContext);

  if (user.type === "ANONYMOUS") {
    return <Navigate replace to={redirectTo} />;
  }

  const requiredRoles = hidesProductCampaigns(user.tenant.tenantType.name)
    ? permissions.productCampaigns
    : allow;

  if (!hasRole(user, requiredRoles)) {
    return <Navigate replace to={redirectTo} />;
  }

  return <>{children}</>;
};

export const RequireCampaignAccess: FunctionComponent<{
  allow: ReadonlyArray<string>;
  redirectTo?: string;
}> = ({ allow, redirectTo = "/", children }) => {
  const user = useContext(UserContext);

  if (user.type === "ANONYMOUS") {
    return <Navigate replace to={redirectTo} />;
  }

  const tenantType = user.tenant.tenantType.name;
  const requiredRoles =
    tenantType === TenantTypeName.MobilePOS
      ? permissions.productCampaigns
      : tenantType === TenantTypeName.WhiteLabel
      ? whiteLabelCampaignRoles
      : allow;

  if (!hasRole(user, requiredRoles)) {
    return <Navigate replace to={redirectTo} />;
  }

  return <>{children}</>;
};
